import {useClickAnalytics} from '@github-ui/use-analytics'
import {ArrowLeftIcon, ArrowRightIcon} from '@primer/octicons-react'
import {
  ActionList,
  Dialog,
  type DialogHeaderProps,
  IconButton,
  useId,
  useResponsiveValue,
  VisuallyHidden,
} from '@primer/react'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {FeatureIcon} from './components/FeatureIcon'
import {FeaturePreviewCard, type FeaturePreviewCardHandle} from './components/FeaturePreviewCard'
import {Tab, TabList, TabPanel, Tabs} from './components/Tabs'
import styles from './FeaturePreviewDialog.module.css'
import {LoadingDialogMobile, LoadingDialogStationary} from './LoadingDialog'
import {type FeaturePreviewObject, type FeaturePreviewResponse, useFeaturePreviewItemsQuery} from './useFeaturePreview'
import {createEvent} from './utils/analytics'

export interface FeaturePreviewDialogProps {
  onClose?: () => void
  login: string
  ref?: React.Ref<HTMLDivElement>
}

export function FeaturePreviewDialog({ref, ...props}: FeaturePreviewDialogProps) {
  const {data, toggleEnrollment: toggleFeature} = useFeaturePreviewItemsQuery({
    login: props.login,
  })
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const isMobile = useResponsiveValue({regular: false, narrow: true}, false)
  const [selectedFeatureSlug, setSelectedFeatureSlug] = useState<string | null>(null)
  const [bannerType, setBannerType] = useState<'error' | 'reload' | undefined>(undefined)
  const selectedFeature = useMemo<FeaturePreviewObject | undefined>(() => {
    const selected = data?.features.find(feature => feature.slug === selectedFeatureSlug)

    if (selected) {
      return selected
    }

    if (!isMobile) {
      return data?.features[0]
    }
  }, [data, selectedFeatureSlug, isMobile])

  const PreviewComponent = isMobile ? FeaturePreviewDialogMobile : FeaturePreviewDialogStationary
  const LoadingComponent = isMobile ? LoadingDialogMobile : LoadingDialogStationary

  const toggleEnrollment = useCallback(
    (feature: FeaturePreviewObject) => {
      void toggleFeature(feature, type => {
        setBannerType(type)
      })
    },
    [toggleFeature],
  )

  const contextValue = useMemo<PreviewDialogContentProps | null>(() => {
    if (!data) {
      return null
    }

    return {
      data,
      showBanner: bannerType,
      toggleEnrollment,
      selectedFeature,
      onSelectFeature: feature => {
        setBannerType(undefined)
        setSelectedFeatureSlug(feature?.slug ?? null)
      },
    }
  }, [data, toggleEnrollment, selectedFeature, bannerType])

  return (
    <FeaturePreviewContext value={contextValue}>
      <Dialog
        ref={ref}
        onClose={() => {
          sendClickAnalyticsEvent(
            createEvent({
              action: 'close_feature_preview_dialog',
              label: 'Close',
            }),
          )

          props.onClose?.()
        }}
        title="Feature Preview"
        subtitle="Get early access to new features and give feedback"
        height="large"
        width="xlarge"
        className={styles.dialog}
        renderHeader={PreviewDialogHeader}
      >
        {!data && <LoadingComponent />}
        {data && (
          <PreviewComponent
            data={data}
            showBanner={bannerType}
            toggleEnrollment={toggleEnrollment}
            selectedFeature={selectedFeature}
            onSelectFeature={f => {
              setBannerType(undefined)
              setSelectedFeatureSlug(f?.slug ?? null)
            }}
          />
        )}
      </Dialog>
    </FeaturePreviewContext>
  )
}

const FeaturePreviewContext = createContext<PreviewDialogContentProps | null>(null)

export interface PreviewDialogContentProps {
  /** The response data from the network for the available feature previews */
  data: FeaturePreviewResponse

  showBanner?: 'error' | 'reload'

  /** Function to toggle enrollment for a feature preview */
  toggleEnrollment: (feature: FeaturePreviewObject) => void

  /** The currently selected feature preview. This will match the selected tab in the UI, and should be an instance from the `data` array */
  selectedFeature: FeaturePreviewObject | undefined

  /** Callback when a feature preview is selected */
  onSelectFeature?: (feature: FeaturePreviewObject | undefined) => void
}

export function FeaturePreviewDialogStationary(props: PreviewDialogContentProps) {
  const {data, toggleEnrollment, selectedFeature, onSelectFeature, showBanner} = props
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  return (
    <Tabs
      value={selectedFeature?.slug ?? data.features[0]?.slug ?? ''}
      onValueChange={({value}) => {
        const feature = data.features.find(f => f.slug === value)
        if (feature && feature !== selectedFeature) {
          sendClickAnalyticsEvent(
            createEvent({
              action: 'select_feature_preview_tab_stationary',
              label: feature.name,
            }),
          )

          onSelectFeature?.(feature)
        }
      }}
    >
      <TabList aria-label="Feature Preview">
        {data.features.map(feature => (
          <Tab key={feature.slug} value={feature.slug} icon={<FeatureIcon enrolled={feature.enrolled} />}>
            {feature.name}
          </Tab>
        ))}
      </TabList>
      {data.features.map(feature => (
        <TabPanel value={feature.slug} key={feature.slug} data-slug={feature.slug}>
          <FeaturePreviewCard
            showBanner={showBanner}
            onUpdate={enrolled => {
              if (enrolled !== feature.enrolled) {
                toggleEnrollment(feature)
              }
            }}
            feature={feature}
          />
        </TabPanel>
      ))}
    </Tabs>
  )
}

export function PreviewDialogHeader(props: PropsWithChildren<DialogHeaderProps>) {
  const isMobile = useResponsiveValue({regular: false, narrow: true}, false) as boolean
  const {title, onClose, dialogLabelId, subtitle, dialogDescriptionId} = props
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  const previewContext = useContext(FeaturePreviewContext)

  const onBackClick = useCallback(() => {
    sendClickAnalyticsEvent(
      createEvent({
        action: 'back_to_feature_preview_list',
        label: 'Back',
      }),
    )

    previewContext?.onSelectFeature?.(undefined)
  }, [previewContext, sendClickAnalyticsEvent])

  const onCloseClick = useCallback(() => {
    sendClickAnalyticsEvent(
      createEvent({
        action: 'close_feature_preview_dialog',
        label: 'Close',
      }),
    )

    onClose('close-button')
  }, [onClose, sendClickAnalyticsEvent])

  const showBackButton = isMobile && previewContext?.selectedFeature !== undefined
  const showSubTitle = Boolean(subtitle) && !showBackButton

  return (
    <Dialog.Header>
      <div className={styles.dialogHeaderInner}>
        {showBackButton && (
          <IconButton
            aria-label="Go back to the feature preview list"
            icon={ArrowLeftIcon}
            variant="invisible"
            onClick={onBackClick}
          />
        )}
        <div className={styles.dialogHeaderContent}>
          <Dialog.Title id={dialogLabelId}>{title ?? 'Feature Preview'}</Dialog.Title>
          {showSubTitle && <Dialog.Subtitle id={dialogDescriptionId}>{subtitle}</Dialog.Subtitle>}
        </div>
        <Dialog.CloseButton aria-label="Close the feature preview dialog" onClose={onCloseClick} />
      </div>
    </Dialog.Header>
  )
}

export function FeaturePreviewDialogMobile(props: PreviewDialogContentProps) {
  const {data, toggleEnrollment, onSelectFeature, selectedFeature, showBanner} = props
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const headingId = useId()
  const featureCardRef = useRef<FeaturePreviewCardHandle>(null)
  const navListRef = useRef<HTMLUListElement>(null)

  const lastSelectedFeatureRef = useRef<FeaturePreviewObject | null>(null)

  const sendAnalytics = (featureName: string) => {
    sendClickAnalyticsEvent(
      createEvent({
        action: 'select_feature_preview_tab_mobile',
        label: featureName,
      }),
    )
  }

  useEffect(() => {
    if (selectedFeature) {
      // Focus the heading of the feature preview card when a new feature is selected
      featureCardRef.current?.focusHeading()
    } else if (navListRef.current && lastSelectedFeatureRef.current) {
      // Focus the nav item of the last selected feature when going back to the list
      const navItem = navListRef.current.querySelector<HTMLLIElement>(
        `[data-feature-slug="${lastSelectedFeatureRef.current.slug}"]`,
      )

      navItem?.focus()
    }
  }, [selectedFeature, navListRef])

  useEffect(() => {
    lastSelectedFeatureRef.current = selectedFeature ?? null
  }, [selectedFeature])

  if (selectedFeature) {
    return (
      <FeaturePreviewCard
        ref={featureCardRef}
        onUpdate={enrolled => {
          if (enrolled !== selectedFeature.enrolled) {
            toggleEnrollment(selectedFeature)
          }
        }}
        feature={selectedFeature}
        showBanner={showBanner}
      />
    )
  }

  return (
    <>
      <VisuallyHidden id={headingId}>Available Feature Previews</VisuallyHidden>
      <nav>
        <ActionList aria-labelledby={headingId} ref={navListRef}>
          {data?.features.map(feature => (
            <ActionList.Item
              as="button"
              type="button"
              key={feature.slug}
              aria-current={selectedFeature === feature ? 'page' : undefined}
              data-feature-slug={feature.slug}
              onSelect={() => {
                sendAnalytics(feature.name)
                onSelectFeature?.(feature)
              }}
            >
              <ActionList.LeadingVisual>
                <FeatureIcon enrolled={feature.enrolled} />
              </ActionList.LeadingVisual>
              {feature.name}
              <ActionList.TrailingVisual>
                <ArrowRightIcon />
              </ActionList.TrailingVisual>
            </ActionList.Item>
          ))}
        </ActionList>
      </nav>
    </>
  )
}

try{ FeaturePreviewDialog.displayName ||= 'FeaturePreviewDialog' } catch {}
try{ FeaturePreviewContext.displayName ||= 'FeaturePreviewContext' } catch {}
try{ FeaturePreviewDialogStationary.displayName ||= 'FeaturePreviewDialogStationary' } catch {}
try{ PreviewDialogHeader.displayName ||= 'PreviewDialogHeader' } catch {}
try{ FeaturePreviewDialogMobile.displayName ||= 'FeaturePreviewDialogMobile' } catch {}